export interface BookingAnalyticsConfig {
  measurementId: string;
  adsConversionTarget: string;
}

interface BookingCompletion {
  bookingId: string;
  rescheduled: boolean;
}

type Gtag = (...args: unknown[]) => void;

type AnalyticsWindow = Window & {
  dataLayer?: unknown[][];
  gtag?: Gtag;
};

const ANALYTICS_CONSENT_COOKIE = "metripact_consent_analytics";
const ADVERTISING_CONSENT_COOKIE = "metripact_consent_advertising";

export const productionAnalyticsConfig: BookingAnalyticsConfig = {
  measurementId: import.meta.env.VITE_METRIPACT_GA_MEASUREMENT_ID?.trim() || "",
  adsConversionTarget:
    import.meta.env.VITE_METRIPACT_GOOGLE_ADS_CONVERSION_TARGET?.trim() || "",
};

function cookieValue(name: string): string | undefined {
  const prefix = `${name}=`;
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length);
}

function hasConsent(name: string): boolean {
  return cookieValue(name) === "granted";
}

export function createBookingAnalytics(config: BookingAnalyticsConfig) {
  let analyticsGranted = false;
  let advertisingGranted = false;
  let initialized = false;
  const trackedCompletions = new Set<string>();

  function initialize(): void {
    if (initialized) return;
    initialized = true;

    analyticsGranted = hasConsent(ANALYTICS_CONSENT_COOKIE);
    advertisingGranted = hasConsent(ADVERTISING_CONSENT_COOKIE);

    const analyticsWindow = window as AnalyticsWindow;
    analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
    analyticsWindow.gtag =
      analyticsWindow.gtag ||
      ((...args: unknown[]) => {
        analyticsWindow.dataLayer?.push(args);
      });

    analyticsWindow.gtag("consent", "default", {
      analytics_storage: analyticsGranted ? "granted" : "denied",
      ad_storage: advertisingGranted ? "granted" : "denied",
      ad_user_data: advertisingGranted ? "granted" : "denied",
      ad_personalization: "denied",
      functionality_storage: "granted",
      security_storage: "granted",
    });

    const adsDestinationId = config.adsConversionTarget.split("/")[0];
    const loaderId =
      (analyticsGranted && config.measurementId) ||
      (advertisingGranted && adsDestinationId) ||
      "";

    if (!loaderId) return;

    if (!document.querySelector("script[data-metripact-google-tag='true']")) {
      const script = document.createElement("script");
      script.async = true;
      script.src =
        "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(loaderId);
      script.dataset.metripactGoogleTag = "true";
      document.head.appendChild(script);
    }

    analyticsWindow.gtag("js", new Date());

    if (analyticsGranted && config.measurementId) {
      analyticsWindow.gtag("config", config.measurementId);
    }

    if (advertisingGranted && adsDestinationId) {
      analyticsWindow.gtag("config", adsDestinationId);
    }
  }

  function trackBookingCompleted(completion: BookingCompletion): void {
    if (completion.rescheduled || trackedCompletions.has(completion.bookingId)) return;
    if (!initialized) initialize();

    const analyticsWindow = window as AnalyticsWindow;
    let tracked = false;

    if (analyticsGranted && config.measurementId && analyticsWindow.gtag) {
      analyticsWindow.gtag("event", "book_demo_completed", {
        provider: "metripact_meet",
        booking_id: completion.bookingId,
      });
      tracked = true;
    }

    if (
      advertisingGranted &&
      config.adsConversionTarget &&
      analyticsWindow.gtag
    ) {
      analyticsWindow.gtag("event", "conversion", {
        send_to: config.adsConversionTarget,
        transaction_id: completion.bookingId,
      });
      tracked = true;
    }

    if (tracked) trackedCompletions.add(completion.bookingId);
  }

  return { initialize, trackBookingCompleted };
}

export const bookingAnalytics = createBookingAnalytics(productionAnalyticsConfig);
